import React, { createContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { getCustomerSelections } from '../api';
import { useCustomer } from './CustomerContext';

export const PriceContext = createContext();

// Initial state factory to avoid recreating objects
const createInitialState = () => ({
  // Identity / house plan fields
  market: '',
  collection: '',
  housePlan: '',
  housePlanId: '',
  housePlanName: '',
  subdivision: '',
  lot: '',
  lotnumber: '',

  // Mortgage details
  mortgageDetails: {
    downPayment: '',
    mortgageRate: '',
    propertyTax: '',
    insurance: '',
    term: '15',
  },

  // Pricing fields — one per pricing-matrix category
  lotPrice: 0,
  houseSelection: 0,

  // Structure
  elevation: 0,
  lotType: 0,
  patioDeck: 0,
  basement: 0,
  garage: 0,

  // Interior
  ceilings: 0,
  windows: 0,
  flooring: 0,
  cabinets: 0,

  // Exterior
  siding: 0,
  roofSoffit: 0,
  porch: 0,
  stone: 0,
  frontDoor: 0,

  // Kitchen
  kitchenBacksplash: 0,
  kitchenFaucet: 0,
  countertops: 0,
  sinks: 0,
  appliances: 0,

  // Bathrooms
  bathroomFixtures: 0,
  shower: 0,
  toilets: 0,

  // Features
  lighting: 0,
  fireplace: 0,
  interior: 0,
  doorHandles: 0,
  garageDoor: 0,
  mechanical: 0,

  // Notes (one per category page)
  elevationNotes: '',
  lotTypeNotes: '',
  patioDeckNotes: '',
  basementNotes: '',
  garageNotes: '',
  ceilingsNotes: '',
  windowsNotes: '',
  flooringNotes: '',
  cabinetsNotes: '',
  sidingNotes: '',
  roofSoffitNotes: '',
  porchNotes: '',
  stoneNotes: '',
  frontDoorNotes: '',
  kitchenBacksplashNotes: '',
  kitchenFaucetNotes: '',
  countertopsNotes: '',
  sinksNotes: '',
  appliancesNotes: '',
  bathroomFixturesNotes: '',
  showerNotes: '',
  toiletsNotes: '',
  lightingNotes: '',
  fireplaceNotes: '',
  interiorNotes: '',
  doorHandlesNotes: '',
  garageDoorNotes: '',
  mechanicalNotes: '',

  // Selection tracking
  selectionsBySection: {},
  selections: {},
  optionDetails: {},
});

// Debounced localStorage save
const useDebouncedSave = () => {
  const timeoutRef = useRef(null);
  
  return useCallback((key, value) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        console.error(`Failed to save ${key} to localStorage`, error);
      }
    }, 500); // 500ms debounce
  }, []);
};

export const PriceProvider = ({ children }) => {
  const { customer } = useCustomer();
  
  // **GET CUSTOMER ID FROM LOCALSTORAGE DIRECTLY** - This is the key fix
  const [customerId, setCustomerId] = useState(() => localStorage.getItem('customerId'));
  const [lastCustomerId, setLastCustomerId] = useState(customerId);
  const debouncedSave = useDebouncedSave();
  
  // **Removed polling mechanism to prevent infinite loops**

  // Initialize state from localStorage or default
  const [priceData, setPriceData] = useState(() => {
    try {
      const savedPriceData = localStorage.getItem('priceData');
      return savedPriceData ? JSON.parse(savedPriceData) : createInitialState();
    } catch (error) {
      console.error('Failed to load price data from localStorage', error);
      return createInitialState();
    }
  });

  // **CRITICAL: Customer change detection with complete reset**
  const resetForNewCustomer = useCallback((newCustomerId) => {
    // ✅ ONLY reset if customer actually changed
    if (newCustomerId === customerId) {
      console.log(`⚠️ Skipping reset - customer ID unchanged: ${newCustomerId}`);
      return;
    }
    
    console.log(`🔄 CRITICAL RESET: ${lastCustomerId} → ${newCustomerId}`);
    
    // Reset to fresh state for new customer
    const freshState = createInitialState();
    setPriceData(freshState);
    
    // Clear ALL localStorage data for clean slate
    localStorage.removeItem('priceData');
    localStorage.setItem('hydrated', 'false');
    
    // Update state tracking
    setCustomerId(newCustomerId);
    setLastCustomerId(newCustomerId);
    
    console.log('✅ CRITICAL RESET completed - fresh state applied');
    console.log('📊 Fresh state:', freshState);
  }, [customerId, lastCustomerId]); // Include customerId in dependencies

  // **Single customer change detection - no infinite loops**
  useEffect(() => {
    const currentCustomerId = localStorage.getItem('customerId');
    
    // Only reset if customer ID actually changed
    if (currentCustomerId && currentCustomerId !== lastCustomerId) {
      console.log(`🔄 Customer ID change detected: ${lastCustomerId} → ${currentCustomerId}`);
      
      // IMMEDIATE complete reset
      const freshState = createInitialState();
      setPriceData(freshState);
      localStorage.removeItem('priceData');
      localStorage.setItem('hydrated', 'false');
      
      // Update tracking
      setCustomerId(currentCustomerId);
      setLastCustomerId(currentCustomerId);
      
      console.log('✅ Customer reset completed');
    }
  }, []); // Empty dependency array - only run once on mount

  // **Simple storage event listener - no dependencies to prevent loops**
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'customerId' && e.newValue) {
        console.log(`🔄 Storage event - Customer ID changed to: ${e.newValue}`);
        setCustomerId(e.newValue);
        setLastCustomerId(e.newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []); // Empty dependency array to prevent infinite loops

  // **Manual trigger function for external use**
  const triggerCustomerCheck = useCallback(() => {
    const currentCustomerId = localStorage.getItem('customerId');
    console.log(`🔄 Manual trigger - checking customer ID: current=${customerId}, localStorage=${currentCustomerId}`);
    
    if (currentCustomerId !== customerId) {
      resetForNewCustomer(currentCustomerId);
    }
  }, [customerId, resetForNewCustomer]);

  // **Memoized initialization function - COMPLETE replacement**
  const initializePriceData = useCallback((data) => {
    if (!data || typeof data !== 'object') {
      console.error('Invalid data provided to initializePriceData');
      return;
    }

    console.log('🔄 Initializing price data with COMPLETE replacement:', data);
    
    // ✅ FIX: Check if data is nested
    const dataToApply = data.selections || data;
    
    const newState = {
      ...createInitialState(),
      ...dataToApply,
      selectionsBySection: dataToApply.selectionsBySection || {},
      selections: dataToApply,
    };
    
    console.log('✅ Setting NEW COMPLETE state:', newState);
    setPriceData(newState);
  }, []);

  // **Load customer data with proper reset first**
  const loadCustomerData = useCallback(async () => {
    if (!customerId) {
      console.log('No customer ID, skipping data load');
      return;
    }

    console.log(`📖 Loading data for customer: ${customerId}`);
    
    try {
      const response = await getCustomerSelections(customerId);
      console.log('🔍 DEBUG: Raw API response:', response);
      
      if (response) {
        const dataToApply = response.selections || response;
        
        // FIX: Map the fields correctly
        const newState = {
          ...createInitialState(),
          ...dataToApply,
          
          // CRITICAL FIXES - Map the fields properly:
          housePlan: dataToApply.housePlanId || dataToApply.housePlan || '',
          housePlanId: dataToApply.housePlanId || '',
          lot: dataToApply.lotnumber || dataToApply.lot || '',
          lotnumber: dataToApply.lotnumber || '',
          houseSelection: Number(dataToApply.houseSelection) || 0,
          lotPrice: Number(dataToApply.lotPrice) || 0,
          
          // Keep these as-is
          market: dataToApply.market || '',
          collection: dataToApply.collection || '',
          subdivision: dataToApply.subdivision || '',
          
          selections: dataToApply.selections || dataToApply,
          selectionsBySection: dataToApply.selectionsBySection || {},
        };
        
        console.log('✅ Fixed state mapping:', {
          housePlan: newState.housePlan,
          lot: newState.lot,
          houseSelection: newState.houseSelection,
          lotPrice: newState.lotPrice
        });
        
        setPriceData(newState);
        localStorage.setItem('hydrated', 'true');
      } else {
        console.log('❌ No data returned for customer - using fresh state');
        setPriceData(createInitialState());
      }
    } catch (error) {
      console.error('Failed to load customer data:', error);
      setPriceData(createInitialState());
    }
  }, [customerId]);

  // Load customer data when customer ID is set and data hasn't been loaded yet
  useEffect(() => {
    if (!customerId) return;
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    if (token && localStorage.getItem('hydrated') === 'false') {
      console.log('🔄 Loading customer data for:', customerId);
      loadCustomerData();
    }
  }, [customerId, loadCustomerData]);

  // Save to localStorage with debouncing
  useEffect(() => {
    if (customerId) { // Only save if we have a customer
      debouncedSave('priceData', priceData);
    }
  }, [priceData, debouncedSave, customerId]);

  // **Memoized update function**
  const updatePriceData = useCallback((updates, options = { replace: false }) => {
    setPriceData((prev) => {
      if (options.replace) {
        return {
          ...updates,
          selectionsBySection: updates.selectionsBySection || {},
        };
      } else {
        // Skip update if values are identical (prevents re-render cascade)
        const hasChanges = Object.keys(updates).some(
          key => prev[key] !== updates[key]
        );
        if (!hasChanges) return prev;

        return {
          ...prev,
          ...updates,
          selectionsBySection: {
            ...(prev.selectionsBySection || {}),
            ...(updates.selectionsBySection || {}),
          },
        };
      }
    });
  }, []); // No priceData dep - uses functional setState pattern

  // **Memoized logout function**
  const logout = useCallback(() => {
    const initialState = createInitialState();
    setPriceData(initialState);
    localStorage.clear();
    setCustomerId(null);
    setLastCustomerId(null);
  }, []);

  // **Calculate total price with memoization**
  const totalPrice = useMemo(() => {
    let total = Number(priceData.lotPrice) || 0;
    
    const priceFields = [
      'houseSelection',
      // Structure
      'elevation', 'lotType', 'patioDeck', 'basement', 'garage',
      // Interior
      'ceilings', 'windows', 'flooring', 'cabinets',
      // Exterior
      'siding', 'roofSoffit', 'porch', 'stone', 'frontDoor',
      // Kitchen
      'kitchenBacksplash', 'kitchenFaucet', 'countertops', 'sinks', 'appliances',
      // Bathrooms
      'bathroomFixtures', 'shower', 'toilets',
      // Features
      'lighting', 'fireplace', 'interior', 'doorHandles', 'garageDoor', 'mechanical',
    ];

    priceFields.forEach(field => {
      total += Number(priceData[field]) || 0;
    });


    // Add diagnostic function to window for debugging
    if (typeof window !== 'undefined') {
      window.diagnosePrices = () => {
        console.log('=== PRICE DIAGNOSTIC ===');
        console.log('Sections with prices:');
        Object.entries(priceData).forEach(([key, value]) => {
          if (value > 0 && typeof value === 'number') {
            console.log(`${key}: $${value}`);
          }
        });
        console.log('Bathroom prices:', priceData.bathroomPrices);
        console.log('Current total:', total);
        return { priceData, total };
      };
      
      // Test function to force price updates
      window.testPriceUpdate = (section, price) => {
        console.log(`FORCE TEST: Setting ${section} to ${price}`);
        updatePriceData({ [section]: price });
      };
      
      // Test all sections for price updates
      window.testAllSections = () => {
        console.log('=== TESTING ALL SECTIONS FOR PRICE UPDATES ===');
        
        const expectedFields = [
          'pivotalSelections', 'exterior', 'entry', 'greatRoom', 'kitchen', 
          'diningRoom', 'bedroom', 'allBedroom1', 'allBedroom2', 
          'allBedroom3', 'allBedroom4', 'laundry', 'basement', 
          'garage', 'roof', 'aboveGrade', 'dropZone', 'wholeHouse', 'customRequests'
        ];
        
        console.log('Current priceData:', priceData);
        console.log('\n✅ Sections with non-zero prices:');
        
        expectedFields.forEach(field => {
          const value = priceData[field];
          if (typeof value === 'number' && value > 0) {
            console.log(`✅ ${field}: $${value.toLocaleString()}`);
          }
        });
        
        console.log('\n❌ Sections with zero/missing prices:');
        expectedFields.forEach(field => {
          const value = priceData[field];
          if (!value || value === 0) {
            console.log(`❌ ${field}: ${value || 0}`);
          }
        });
        
        console.log('\n🛁 Bathroom prices (special structure):');
        if (priceData.bathroomPrices) {
          Object.entries(priceData.bathroomPrices).forEach(([key, value]) => {
            if (value > 0) {
              console.log(`✅ ${key}: $${value.toLocaleString()}`);
            } else {
              console.log(`❌ ${key}: ${value || 0}`);
            }
          });
        }
        
        console.log('\n📊 Total price:', total);
        
        return {
          working: expectedFields.filter(f => priceData[f] > 0),
          notWorking: expectedFields.filter(f => !priceData[f] || priceData[f] === 0),
          bathroomPrices: priceData.bathroomPrices || {}
        };
      };
      
      // Identify sections that don't follow standard pattern
      window.identifyProblemSections = () => {
        console.log('=== IDENTIFYING PROBLEM SECTIONS ===');
        
        const standardSections = {
          // Working sections with SelectionPageTemplate
          'pivotalSelections': 'LocationPivot.js',
          'exterior': 'LocationExterior.js', 
          'entry': 'LocationEntry.js',
          'greatRoom': 'LocationGreatRoom.js',
          'kitchen': 'LocationKitchen.js',
          'diningRoom': 'LocationDiningRoom.js',
          'bedroom': 'LocationBedrooms.js',
          'allBedroom1': 'LocationAllBedroom1.js',
          'allBedroom2': 'LocationAllBedroom2.js',
          'allBedroom3': 'LocationAllBedroom3.js',
          'allBedroom4': 'LocationAllBedroom4.js',
          'laundry': 'LocationLaundryRoom.js',
          'garage': 'LocationGarage.js',
          'roof': 'LocationRoof.js',
          'aboveGrade': 'LocationAboveGrade.js',
          'dropZone': 'LocationDropZone.js',
          'wholeHouse': 'LocationWholeHouse.js',
          'customRequests': 'LocationCustomRequests.js'
        };
        
        const customSections = {
          // Sections with custom implementations (NO apiPrefix)
          'basement': 'LocationBasement.js - Custom implementation',
          'bathroom': 'LocationBathrooms.js - Custom implementation (uses bathroomPrices instead)'
        };
        
        console.log('📋 Standard sections (use SelectionPageTemplate + apiPrefix):');
        Object.entries(standardSections).forEach(([field, file]) => {
          const value = priceData[field];
          const status = value > 0 ? '✅' : '❌';
          console.log(`${status} ${field}: ${value || 0} (${file})`);
        });
        
        console.log('\n🔧 Custom sections (may need fixing):');
        Object.entries(customSections).forEach(([field, file]) => {
          const value = priceData[field];
          const status = value > 0 ? '✅' : '❌';
          console.log(`${status} ${field}: ${value || 0} (${file})`);
        });
        
        console.log('\n🛁 Bathroom prices (special handling):');
        if (priceData.bathroomPrices) {
          Object.entries(priceData.bathroomPrices).forEach(([key, value]) => {
            const status = value > 0 ? '✅' : '❌';
            console.log(`${status} ${key}: ${value || 0}`);
          });
        }
        
        const problemSections = Object.keys(standardSections).filter(f => !priceData[f] || priceData[f] === 0);
        console.log('\n🚨 Problem sections that should be working:', problemSections);
        
        return {
          standardSections,
          customSections,
          problemSections
        };
      };
    }

    return total;
  }, [priceData]);

  // **Memoized context value with setCustomerId exported**
  const contextValue = useMemo(() => ({
    priceData,
    setPriceData,
    updatePriceData,
    logout,
    totalPrice,
    initializePriceData,
    loadCustomerData,
    triggerRefresh: loadCustomerData,
    triggerCustomerCheck,
    resetForNewCustomer,
    customerId,
    setCustomerId  // ✅ FIXED: Export setCustomerId for Login.js
  }), [
    priceData, 
    updatePriceData, 
    logout, 
    totalPrice, 
    initializePriceData, 
    loadCustomerData, 
    triggerCustomerCheck, 
    resetForNewCustomer, 
    customerId,
    setCustomerId  // ✅ FIXED: Include in dependencies
  ]);

  return (
    <PriceContext.Provider value={contextValue}>
      {children}
    </PriceContext.Provider>
  );
};